import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiResponse, ProblemDetails } from './types';

/** A failure carrying the Barah Pay error code and correlation id (spec sections 51 and 52). */
export class BarahPayApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly errorCode?: string,
    readonly correlationId?: string,
    readonly validationErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'BarahPayApiError';
  }

  /** True when retrying the same request could reasonably succeed. */
  get isTransient(): boolean {
    return this.status === 429 || this.status === 503 || this.status >= 500;
  }
}

export interface TokenStore {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  setTokens(accessToken: string, refreshToken: string): void;
  clear(): void;
}

export interface ApiClientOptions {
  baseUrl: string;
  tokenStore?: TokenStore;
  /** Called when the session can no longer be refreshed, so the app can return to sign-in. */
  onUnauthorized?: () => void;
  /** Returns the current UI language, sent so the API can localise customer-facing text. */
  getLanguage?: () => string;
}

let correlationCounter = 0;

/** A client-side correlation id, so a UI action can be traced end to end (spec section 38). */
function newCorrelationId(): string {
  correlationCounter += 1;
  const random = Math.random().toString(36).slice(2, 10);
  return `WEB-${Date.now().toString(36)}-${random}-${correlationCounter}`;
}

/**
 * Builds the shared axios instance: bearer auth, correlation ids, language, ProblemDetails
 * translation, and a single-flight refresh on 401.
 */
export function createApiClient(options: ApiClientOptions): AxiosInstance {
  const client = axios.create({
    baseURL: options.baseUrl.replace(/\/$/, ''),
    timeout: 60_000,
    headers: { 'Content-Type': 'application/json' },
    // ASP.NET Core binds a collection from repeated keys (`status=A&status=B`), not from axios's
    // default `status[]=A` bracket form, so the serializer is explicit.
    paramsSerializer: {
      serialize: (params: Record<string, unknown>) => {
        const search = new URLSearchParams();

        for (const [key, value] of Object.entries(params)) {
          if (value === undefined || value === null || value === '') {
            continue;
          }

          if (Array.isArray(value)) {
            value
              .filter((entry) => entry !== undefined && entry !== null && entry !== '')
              .forEach((entry) => search.append(key, String(entry)));
            continue;
          }

          search.append(key, String(value));
        }

        return search.toString();
      },
    },
  });

  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = options.tokenStore?.getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers['X-Correlation-ID'] = newCorrelationId();

    const language = options.getLanguage?.();
    if (language) {
      config.headers['Accept-Language'] = language;
    }

    return config;
  });

  // A single refresh runs at a time; concurrent 401s wait for it rather than each starting their own.
  let refreshInFlight: Promise<boolean> | null = null;

  async function refreshOnce(): Promise<boolean> {
    if (!options.tokenStore) {
      return false;
    }

    const refreshToken = options.tokenStore.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    refreshInFlight ??= (async () => {
      try {
        // A bare axios call, so this request cannot recurse through this interceptor.
        const response = await axios.post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
          `${options.baseUrl.replace(/\/$/, '')}/api/v1/auth/refresh`,
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } },
        );

        const data = response.data.data;
        options.tokenStore!.setTokens(data.accessToken, data.refreshToken);
        return true;
      } catch {
        options.tokenStore!.clear();
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();

    return refreshInFlight;
  }

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (!axios.isAxiosError(error)) {
        throw error;
      }

      const status = error.response?.status ?? 0;
      const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;

      // One refresh attempt per request, and never for the auth endpoints themselves.
      const isAuthCall = typeof original?.url === 'string' && original.url.includes('/api/v1/auth/');

      if (status === 401 && original && !original._retried && !isAuthCall) {
        original._retried = true;

        if (await refreshOnce()) {
          return client.request(original);
        }

        options.onUnauthorized?.();
      }

      if (status === 401 && isAuthCall) {
        options.tokenStore?.clear();
      }

      const problem = error.response?.data as ProblemDetails | undefined;

      throw new BarahPayApiError(
        problem?.detail ?? problem?.title ?? error.message ?? 'The request failed.',
        status,
        problem?.errorCode,
        problem?.correlationId ?? (error.response?.headers?.['x-correlation-id'] as string | undefined),
        problem?.errors,
      );
    },
  );

  return client;
}

/** Unwraps the section 51 success envelope. */
export function unwrap<T>(payload: ApiResponse<T> | T): T {
  if (payload && typeof payload === 'object' && 'success' in payload && 'data' in payload) {
    return (payload as ApiResponse<T>).data;
  }

  return payload as T;
}

/**
 * Drops empty query values so the URL carries only real filters. Arrays are kept as arrays; the client's
 * parameter serializer turns them into repeated keys.
 */
export function toQuery(params: Record<string, unknown>): Record<string, string | string[]> {
  const query: Record<string, string | string[]> = {};

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }

    if (Array.isArray(value)) {
      const entries = value
        .filter((entry) => entry !== undefined && entry !== null && entry !== '')
        .map((entry) => String(entry));

      if (entries.length > 0) {
        query[key] = entries;
      }

      continue;
    }

    query[key] = String(value);
  }

  return query;
}
